<template>
    <aside>
        <m-welcome-carrier-registration-status
            v-if="has_tcr_step"
            :tcr_step_number="tcr_step_number"
            :carrier_registration_status="carrier_registration_status"
        />

        <!-- User Training Experience -->
        <div class="section-wrapper section-wrapper--bordered pb-6 mb-3 d-flex flex-column justify-center">
            <v-card flat>
                <v-card-title class="font-weight-medium pt-0 mb-1 px-0 d-flex align-center">
                    Training Session
                </v-card-title>

                <v-card-subtitle class="text-body-2 pa-0">
                    Ready to launch RedChirp? Schedule a training session with our team to get personalized help setting
                    up your account and learning how to use RedChirp effectively.
                </v-card-subtitle>

                <v-card-text class="pa-0">
                    <div class="d-flex flex-column align-center">
                        <template v-if="!ready_for_training && !has_training_scheduled">
                            <v-btn
                                color="primary"
                                class="font-weight-medium mt-4"
                                @click="onClickReadyForTraining"
                            >
                                I'm Ready for Training
                            </v-btn>
                        </template>

                        <template v-else>
                            <div class="d-flex align-center primary--text font-weight-medium my-1 pt-3">
                                <v-icon
                                    color="primary"
                                    size="32"
                                    class="mr-2"
                                    aria-hidden="true"
                                >
                                    mdi-check-circle
                                </v-icon>

                                <div v-if="!has_training_scheduled">
                                    You've indicated that you're ready for training. We'll contact you to schedule a
                                    Zoom session!
                                </div>
                                <div v-else-if="is_training_in_future">
                                    Your training is scheduled for {{ formatted_training_time }}.
                                </div>
                                <div v-else>
                                    Your training was completed on {{ formatted_training_time }}.
                                </div>
                            </div>
                        </template>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- Admin Training Experience -->
        <div
            v-if="this.$rc.$state.isAuthActorSystemAdmin()"
            class="section-wrapper pb-6 mb-3 d-flex flex-column justify-center"
        >
            <v-card flat>
                <v-card-title class="font-weight-medium pa-0 d-flex align-center">
                    Schedule Training (Staff Only)&nbsp;<sup
                        class="restricted-indicator"
                        role="img"
                        title="Staff Only"
                    ></sup>
                </v-card-title>

                <v-card-text class="pa-0">
                    <!-- Edit Component - when no training scheduled OR when editing future training -->
                    <div
                        v-if="!has_training_scheduled || editing_training_time"
                        class="shaded-light pa-3 pt-2 pb-4 mt-3"
                    >
                        <m-edit-instant
                            label="Training Time"
                            :value="profile.organization_admin_settings.time_training"
                            :timezone="this.$rc.$state.getPrimaryTimezone()"
                            @input="onChangeTimeTraining"
                            optional
                        />

                        <div
                            v-if="is_training_in_future && editing_training_time"
                            class="d-flex justify-end mt-2"
                        >
                            <v-btn
                                text
                                small
                                @click="editing_training_time = false"
                            >
                                Cancel
                            </v-btn>
                        </div>
                    </div>

                    <!-- Status Message - when training is scheduled -->
                    <div
                        v-else-if="has_training_scheduled"
                        class="d-flex align-center primary--text font-weight-medium my-1 pt-3"
                    >
                        <v-icon
                            color="primary"
                            size="28"
                            class="mr-2"
                            aria-hidden="true"
                        >
                            mdi-check-circle
                        </v-icon>

                        <div class="flex-grow-1">
                            <div
                                v-if="is_training_in_future"
                                class="primary--text"
                            >
                                Training is scheduled for {{ formatted_training_time }}
                            </div>
                            <div v-else>
                                Training was completed on {{ formatted_training_time }}
                            </div>
                        </div>

                        <v-btn
                            text
                            small
                            color="primary"
                            class="ml-2"
                            @click="editing_training_time = true"
                        >
                            <v-icon
                                small
                                left
                                aria-hidden="true"
                            >
                                mdi-pencil
                            </v-icon>
                            Edit
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- Additional Setup Tips -->
        <div class="section-wrapper pb-6 mb-3 d-flex flex-column justify-center">
            <v-card flat>
                <v-card-title class="font-weight-medium pa-0 d-flex align-center">
                    Additional Setup Tips
                </v-card-title>

                <v-divider class="mt-2 mb-1" />

                <v-card-text class="pa-0">
                    <ul class=" mt-0 mb-1 px-2 pb-4 pt-3">
                        <li class="d-flex align-center">
                            <v-icon
                                color="primary"
                                class="mr-3"
                                aria-hidden="true"
                            >
                                mdi-bookmark-outline
                            </v-icon>

                            <div>
                                <strong>Bookmark RedChirp</strong>
                                <div>
                                    Bookmark this page for quick access. You won't need to sign in every time you
                                    come back.
                                </div>
                            </div>
                        </li>

                        <li class="d-flex align-center">
                            <v-icon
                                color="primary"
                                class="mr-3"
                                aria-hidden="true"
                            >
                                mdi-cellphone
                            </v-icon>

                            <div>
                                <strong>Add RedChirp to your phone</strong>

                                <div>
                                    RedChirp isn't in the App Store yet. Watch these quick videos to add it to your
                                    <a
                                        href="https://vimeo.com/481839003"
                                        target="_blank"
                                    >
                                        iPhone
                                    </a>
                                    or
                                    <a
                                        href="https://vimeo.com/481841786"
                                        target="_blank"
                                    >
                                        Android.
                                    </a>
                                </div>
                            </div>
                        </li>

                        <li
                            v-if="profile?.bot_endpoint"
                            class="d-flex align-center"
                        >
                            <v-icon
                                color="primary"
                                class="mr-3"
                                aria-hidden="true"
                            >
                                mdi-robot-outline
                            </v-icon>

                            <div>
                                <strong>Add RedChirp Bot to your contacts</strong>
                                <div>
                                    {{ $rc.pretty(profile.bot_endpoint, 'endpoint') }} — Save as "RedChirp Bot".
                                    You'll get text notifications only when needed, and only during your allowed times.
                                </div>
                            </div>
                        </li>
                    </ul>
                </v-card-text>
            </v-card>

            <v-divider class="mb-5" />

            <div class="d-flex align-center justify-center mb-2">
                <v-icon
                    color="primary"
                    class="mr-2"
                    aria-hidden="true"
                >
                    mdi-help-circle-outline
                </v-icon>
                <span>Need help with anything?</span>
            </div>

            <v-btn
                color="primary"
                class="mt-1 font-weight-medium"
                @click="$rc.contactSupport('Welcome screen')"
                outlined
            >
                <v-icon
                    left
                    aria-hidden="true"
                >
                    mdi-email
                </v-icon>
                <strong>Contact RedChirp Support</strong>
            </v-btn>
        </div>

        <v-dialog
            v-model="show_training_warning_dialog"
            max-width="500"
        >
            <v-card>
                <v-card-title class="font-weight-medium">
                    Heads Up!
                </v-card-title>

                <v-card-text v-html="training_warning_message">
                </v-card-text>

                <v-card-actions class="justify-end">
                    <v-btn
                        text
                        @click="show_training_warning_dialog = false"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        color="primary"
                        @click="show_training_warning_dialog = false; markReadyForTraining()"
                    >
                        Continue Anyway
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </aside>
</template>

<script>
import { DateTime } from 'luxon';
import mWelcomeCarrierRegistrationStatus from "./welcome-carrier-registration-status.vue";

export default {
    name: "WelcomeSidebar",

    components: {
        mWelcomeCarrierRegistrationStatus,
    },

    props: {
        profile: {
            type: Object,
            default: null,
        },

        steps_complete_percentage: {
            type: Number,
            default: 0,
        },

        tcr_setup_complete: {
            type: Boolean,
            default: false,
        },

        tcr_vetting_approved: {
            type: Boolean,
            default: false,
        },

        has_tcr_step: {
            type: Boolean,
            default: false,
        },

        tcr_step_number: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            marked_ready_for_training: false,
            editing_training_time: false,
            show_training_warning_dialog: false,
            training_warning_message: '',
        };
    },

    computed: {
        carrier_registration_status() {
            if (this.profile.campaign_registry_settings.default_tcr_campaign_id_bandwidth) {
                return 'approved';
            }
            if (this.profile.setup_tasks.tcr_setup_data_complete) {
                return 'pending';
            }
            return 'missing';
        },

        ready_for_training() {
            return this.profile.setup_tasks?.ready_for_training;
        },

        has_training_scheduled() {
            return Boolean(this.profile.organization_admin_settings?.time_training);
        },

        formatted_training_time() {
            if (!this.has_training_scheduled) return null;

            const training_time = this.profile.organization_admin_settings?.time_training;
            if (!training_time) return null;

            let dt = DateTime.fromMillis(training_time).setZone('America/Phoenix');
            return dt.toISODate() + ' @ ' + dt.toLocaleString(DateTime.TIME_SIMPLE) + ' (America/Phoenix)';
        },

        is_training_in_future() {
            if (!this.has_training_scheduled) return false;

            const training_time = this.profile.organization_admin_settings?.time_training;
            if (!training_time) return false;

            return DateTime.fromMillis(training_time) > DateTime.now();
        },
    },

    methods: {
        onChangeTimeTraining(time) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PATCH: /api/v1/admin/organization-admin-settings',

                params: {
                    organization_id: this.profile.organization_account.organization_id,
                    time_training: time,
                },

                on_success: () => {
                    if (!this.profile.setup_tasks.ready_for_training) {
                        this.profile.setup_tasks.ready_for_training = true;
                    }

                    this.editing_training_time = false;
                    this.$emit('update');
                },

                on_error: 'Unable to set training time',
            });
        },

        onClickReadyForTraining() {
            if (this.has_tcr_step) {
                if (!this.tcr_setup_complete || this.steps_complete_percentage < 80) {
                    let message = "<p>To get the most out of your training, <strong>we recommend completing as many steps as possible before scheduling</strong>. Are you sure you want to continue with incomplete steps?</p>"
                    if (!this.tcr_setup_complete) {
                        message += `<p>Keep in mind that step ${this.tcr_step_number} is out of our control and can take up to 10 business days to approve once submitted.</p>`;
                    }
                    this.training_warning_message = message;
                    this.show_training_warning_dialog = true;
                    return;
                }
                // enable this once we have Calendly link in place and are allowing users to actually schedule their training
                // else if (!this.tcr_vetting_approved) {
                //     this.training_warning_message = "Heads up - you've submitted your brand for carrier approval, but it's still pending. When scheduling your training, be sure to select a training far enough in the future to give time for this approval. Approvals are out of our control and can take up to 10 business days.";
                //     this.show_training_warning_dialog = true;
                //     return;
                // }
            }

            this.markReadyForTraining();
        },

        markReadyForTraining() {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/welcome/tasks/ready_for_training',

                params: {
                    employee_id: this.profile.employee_account.employee_id,
                    value: true,
                },

                on_success: () => {
                    this.$emit('update');
                },

                on_error: 'Unable to update settings',
            });
        },
    }
}
</script>

<style lang="scss" scoped>
.section-wrapper {
    border: 1px solid rgba(0, 0, 0, 0.2);
    padding: 1rem;
    padding-bottom: 0;
    border-radius: 0.5rem;
    margin-top: 1rem;

    &--bordered {
        border: 2px solid var(--v-primary-base);
    }

    ul {
        list-style: none;
        padding: 0;
        padding-left: .25rem;
        margin-top: 1rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
}
</style>